<!-- eslint-disable vue/no-bare-strings-in-template -->
<script>
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import contactsAPI from 'dashboard/api/contacts';
import messageAPI from 'dashboard/api/inbox/message';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'ForwardContactModal',
  components: {
    Avatar,
    Spinner,
    EsqueletoLista,
    EstadoVazio,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      searchQuery: '',
      contacts: [],
      isLoading: false,
      isForwarding: false,
      searchTimeout: null,
      currentPage: 1,
      hasMore: true,
      isLoadingMore: false,
      selectedIds: new Set(),
      isSearchMode: false,
      falhouCarga: false,
    };
  },
  computed: {
    selectedCount() {
      return this.selectedIds.size;
    },
    allSelected() {
      return (
        this.contacts.length > 0 &&
        this.contacts.every(c => this.selectedIds.has(c.id))
      );
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.reset();
        this.loadInitialContacts();
      }
    },
    searchQuery(value) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.contacts = [];
        this.currentPage = 1;
        this.hasMore = true;
        this.selectedIds = new Set();
        if (value.trim()) {
          this.isSearchMode = true;
          this.fetchSearchContacts(value.trim(), 1);
        } else {
          this.isSearchMode = false;
          this.loadInitialContacts();
        }
      }, 300);
    },
  },
  methods: {
    reset() {
      this.searchQuery = '';
      this.contacts = [];
      this.currentPage = 1;
      this.hasMore = true;
      this.selectedIds = new Set();
      this.isSearchMode = false;
    },
    onClose() {
      this.$emit('close');
    },
    async loadInitialContacts() {
      this.isLoading = true;
      this.falhouCarga = false;
      try {
        const response = await contactsAPI.get(1);
        const payload = response.data?.payload || [];
        this.contacts = payload;
        this.currentPage = 1;
        this.hasMore = payload.length >= 15;
      } catch {
        // Sem isto, a rede fora aparecia como "nenhum contato encontrado".
        this.contacts = [];
        this.falhouCarga = true;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchSearchContacts(query, page) {
      if (page === 1) {
        this.isLoading = true;
        this.falhouCarga = false;
      } else {
        this.isLoadingMore = true;
      }
      try {
        const response = await contactsAPI.search(query, page);
        const payload = response.data?.payload || [];
        if (page === 1) {
          this.contacts = payload;
        } else {
          this.contacts = [...this.contacts, ...payload];
        }
        this.hasMore = payload.length >= 15;
        this.currentPage = page;
      } catch {
        if (page === 1) {
          this.contacts = [];
          this.falhouCarga = true;
        }
      } finally {
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    },
    async loadMore() {
      if (this.isLoadingMore || !this.hasMore || this.isLoading) return;
      const nextPage = this.currentPage + 1;
      this.isLoadingMore = true;
      try {
        let response;
        if (this.isSearchMode) {
          response = await contactsAPI.search(
            this.searchQuery.trim(),
            nextPage
          );
        } else {
          response = await contactsAPI.get(nextPage);
        }
        const payload = response.data?.payload || [];
        this.contacts = [...this.contacts, ...payload];
        this.hasMore = payload.length >= 15;
        this.currentPage = nextPage;
      } catch {
        // silently ignore load-more errors
      } finally {
        this.isLoadingMore = false;
      }
    },
    onScroll(event) {
      const el = event.target;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      if (nearBottom) {
        this.loadMore();
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedIds = new Set();
      } else {
        this.selectedIds = new Set(this.contacts.map(c => c.id));
      }
    },
    toggleSelect(contactId) {
      const next = new Set(this.selectedIds);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      this.selectedIds = next;
    },
    isSelected(contactId) {
      return this.selectedIds.has(contactId);
    },
    async forwardSelected() {
      if (this.isForwarding || this.selectedIds.size === 0) return;
      this.isForwarding = true;
      const ids = Array.from(this.selectedIds);
      try {
        await Promise.all(
          ids.map(id => messageAPI.forwardContact(this.conversationId, id))
        );
        // Um contato só: "1 contatos encaminhados" era a frase errada.
        useAlert(
          ids.length === 1
            ? this.$t('CONVERSATION.FORWARD_CONTACT.SUCCESS')
            : this.$t('CONVERSATION.FORWARD_CONTACT.SUCCESS_MULTIPLE', {
                count: ids.length,
              })
        );
        this.onClose();
      } catch {
        useAlert(this.$t('CONVERSATION.FORWARD_CONTACT.ERROR'));
      } finally {
        this.isForwarding = false;
      }
    },
    contactSubtext(contact) {
      const parts = [];
      if (contact.phone_number) parts.push(contact.phone_number);
      if (contact.email) parts.push(contact.email);
      return parts.join(' · ');
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <!-- Compartilhar contatos NESTA conversa (vai o cartão do contato para o
         cliente). Casca do produto, topo teal — é sobre PESSOA.

         🔴 CLICAR NA LINHA MARCA; O ENVIO É PELO BOTÃO (16/09/2026).
         Antes, clicar no nome enviava na hora, sem confirmação: um clique no
         contato errado mandava para o cliente o telefone de outra pessoa. O
         gesto agora é o mesmo do "Encaminhar mensagens" — marca, confere, envia. -->
    <Transition name="advup-saida">
      <div v-if="show" class="advup-modal-fundo" @click.self="onClose">
        <div
          v-modal-acessivel="onClose"
          class="advup-modal advup-modal--claro advup-modal--teto fct"
        >
          <div class="advup-modal__topo advup-modal__topo--teal">
            <!-- Topo claro da Agenda (§5.10), como nos outros módulos — era o
                 degradê escuro (05/10/2026). -->
            <i
              class="advup-modal__marca i-lucide-user-round-plus"
              aria-hidden="true"
            />
            <span class="advup-modal__selo" aria-hidden="true">
              <i class="i-lucide-user-round-plus" />
            </span>
            <div class="min-w-0 flex-1">
              <h2 class="advup-modal__titulo">
                {{ $t('CONVERSATION.FORWARD_CONTACT.TITLE') }}
              </h2>
              <p class="advup-modal__sub advup-modal__sub--solta">
                {{ $t('CONVERSATION.FORWARD_CONTACT.SUBTITLE') }}
              </p>
            </div>
            <button
              type="button"
              class="advup-modal__fechar"
              :aria-label="$t('CONVERSATION.FORWARD_CONTACT.CANCEL')"
              @click="onClose"
            >
              <i class="i-lucide-x" />
            </button>
          </div>

          <div class="advup-modal__corpo fct-corpo">
            <div class="advup-busca advup-busca--bloco">
              <i class="advup-busca__lupa i-lucide-search" aria-hidden="true" />
              <input
                v-model="searchQuery"
                type="text"
                class="advup-busca__campo reset-base"
                :aria-label="$t('CONVERSATION.FORWARD_CONTACT.SEARCH')"
                :placeholder="$t('CONVERSATION.FORWARD_CONTACT.SEARCH')"
              />
            </div>

            <label v-if="contacts.length > 0" class="fct-todos">
              <input
                type="checkbox"
                class="advup-caixa"
                :checked="allSelected"
                @change="toggleSelectAll"
              />
              {{ $t('CONVERSATION.FORWARD_CONTACT.SELECT_ALL') }}
            </label>

            <div class="fct-lista" @scroll="onScroll">
              <EsqueletoLista v-if="isLoading" :linhas="6" :altura="52" />

              <EstadoVazio
                v-else-if="falhouCarga"
                tom="erro"
                icone="i-lucide-wifi-off"
                :titulo="$t('CONVERSATION.FORWARD_CONTACT.LOAD_ERROR')"
              >
                <button
                  type="button"
                  class="advup-btn advup-btn--vazado"
                  @click="loadInitialContacts"
                >
                  {{ $t('CONVERSATION.FORWARD_CONTACT.RETRY') }}
                </button>
              </EstadoVazio>

              <EstadoVazio
                v-else-if="contacts.length === 0"
                icone="i-lucide-users"
                :titulo="$t('CONVERSATION.FORWARD_CONTACT.EMPTY')"
                :dica="$t('CONVERSATION.FORWARD_CONTACT.EMPTY_DESCRIPTION')"
              />

              <ul v-else class="fct-itens">
                <li
                  v-for="(contact, i) in contacts"
                  :key="contact.id"
                  class="advup-entra"
                  :style="{ '--i': Math.min(i, 10) }"
                >
                  <label class="fct-linha">
                    <input
                      type="checkbox"
                      class="advup-caixa"
                      :checked="isSelected(contact.id)"
                      @change="toggleSelect(contact.id)"
                    />
                    <Avatar
                      :src="contact.thumbnail || ''"
                      :name="contact.name || '?'"
                      :size="32"
                      rounded
                    />
                    <span class="fct-linha__txt">
                      <span class="fct-linha__nome">{{ contact.name }}</span>
                      <span
                        v-if="contactSubtext(contact)"
                        class="fct-linha__sub"
                      >
                        {{ contactSubtext(contact) }}
                      </span>
                    </span>
                  </label>
                </li>
                <li v-if="isLoadingMore" class="fct-mais">
                  <Spinner :size="16" class="text-n-blue-11" />
                </li>
              </ul>
            </div>
          </div>

          <div class="advup-modal__rodape advup-modal__rodape--acoes">
            <span class="fct-conta" aria-live="polite">
              <b class="fct-conta__num">{{ selectedCount }}</b>
              {{
                $t('CONVERSATION.FORWARD_CONTACT.SELECTED_COUNT', selectedCount)
              }}
            </span>
            <span class="advup-modal__espaco" />
            <button
              type="button"
              class="advup-btn advup-btn--vazado"
              @click="onClose"
            >
              {{ $t('CONVERSATION.FORWARD_CONTACT.CANCEL') }}
            </button>
            <button
              type="button"
              class="advup-btn-forte"
              :disabled="selectedCount === 0 || isForwarding"
              @click="forwardSelected"
            >
              <i
                :class="
                  isForwarding
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-send'
                "
                aria-hidden="true"
              />
              {{ $t('CONVERSATION.FORWARD_CONTACT.SEND') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fct {
  width: min(520px, calc(100vw - 2rem));
}

.fct-corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  min-height: 0;
}

.fct-todos {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-1) var(--esp-3);
  font-size: var(--txt-corpo);
  color: rgb(var(--slate-11));
  cursor: pointer;
  user-select: none;
}

.fct-lista {
  overflow-y: auto;
  min-height: 16rem;
  max-height: 22rem;
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-4);
}

.fct-itens {
  margin: 0;
  padding: 0;
  list-style: none;
}

.fct-itens > li + li {
  border-top: 1px solid rgb(var(--slate-3));
}

/* Teal = pessoa. A faixa cresce no hover e enche quando marcada — o mesmo
   gesto do encaminhar mensagens. */
.fct-linha {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  padding: var(--esp-2) var(--esp-3);
  cursor: pointer;
  transition: background-color var(--mov-1) var(--mov-curva);
}

.fct-linha::before {
  content: '';
  position: absolute;
  inset: var(--esp-2) auto var(--esp-2) 0;
  width: 3px;
  border-radius: 0 var(--r-1) var(--r-1) 0;
  background: rgb(var(--teal-9));
  scale: 1 0;
  transition: scale var(--mov-2) var(--mov-curva-pulo);
}

.fct-linha:hover {
  background: rgb(var(--slate-2));
}

.fct-linha:hover::before {
  scale: 1 0.5;
}

.fct-linha:has(.advup-caixa:checked) {
  background: rgb(var(--teal-2));
}

.fct-linha:has(.advup-caixa:checked)::before {
  scale: 1 1;
}

.fct-linha__txt {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.fct-linha__nome {
  overflow: hidden;
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fct-linha__sub {
  overflow: hidden;
  font-size: var(--txt-nota);
  color: rgb(var(--slate-10));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fct-mais {
  display: flex;
  justify-content: center;
  padding: var(--esp-3);
}

.fct-conta {
  display: inline-flex;
  align-items: baseline;
  gap: var(--esp-1);
  font-size: var(--txt-corpo);
  color: rgb(var(--slate-11));
}

.fct-conta__num {
  font-size: var(--txt-bloco);
  font-weight: var(--peso-forte);
  color: rgb(var(--teal-11));
  font-variant-numeric: tabular-nums;
}

@media (prefers-reduced-motion: reduce) {
  .fct-linha,
  .fct-linha::before {
    transition: none;
  }
}
</style>
